import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  Avatar,
  Chip,
  Autocomplete,
  FormControl,
  InputLabel,
  Box,
  Divider,
  useTheme,
} from '@mui/material';
import { TASK_STATUS, TASK_PRIORITY } from './taskModels';
import TaskStatusChip from './TaskStatusChip';
import PriorityChip from './PriorityChip';

const AddEditTaskDialog = ({
  open,
  onClose,
  onSave,
  selectedTask,
  taskTitle,
  setTaskTitle,
  taskDescription,
  setTaskDescription,
  taskStatus,
  setTaskStatus,
  taskPriority,
  setTaskPriority,
  taskAgendaId,
  setTaskAgendaId,
  taskStartTime,
  setTaskStartTime,
  taskEndTime,
  setTaskEndTime,
  selectedAssignees,
  setSelectedAssignees,
  eventParticipants,
  agendas,
}) => {
  const theme = useTheme();

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '95vw', sm: 600, md: 700 },
          maxWidth: '95vw',
          maxHeight: '85vh',
          borderRadius: 3,
          boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
          p: 0,
        }
      }}
    >
      <DialogTitle sx={{ 
        fontWeight: 600, 
        fontSize: { xs: 20, sm: 24 }, 
        pb: 2, 
        px: { xs: 3, sm: 4 }, 
        pt: { xs: 3, sm: 4 },
        borderBottom: `1px solid ${theme.palette.divider}`
      }}>
        {selectedTask ? 'Edit Task' : 'Add Task'}
      </DialogTitle>

      <DialogContent 
        sx={{ 
          p: { xs: 3, sm: 4 }, 
          pt: { xs: 3, sm: 4 },
          maxHeight: 'calc(85vh - 140px)',
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.grey[400],
            borderRadius: '3px',
          }
        }}
      >
        {/* Task Title & Description */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 4 }}>
          <TextField
            label="Task Title"
            value={taskTitle}
            onChange={(e) => setTaskTitle(e.target.value)}
            fullWidth
            autoFocus
            variant="outlined"
            InputLabelProps={{ sx: { fontWeight: 500 } }}
            sx={{ 
              '& .MuiOutlinedInput-root': {
                minHeight: 56,
                '& fieldset': {
                  borderColor: theme.palette.grey[300],
                },
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              }
            }}
          />
          <TextField
            label="Description"
            value={taskDescription}
            onChange={(e) => setTaskDescription(e.target.value)}
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            InputLabelProps={{ sx: { fontWeight: 500 } }}
            sx={{ 
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: theme.palette.grey[300],
                },
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              }
            }}
          />
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Status & Priority */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 3, 
          mb: 4 
        }}>
          <FormControl fullWidth>
            <InputLabel sx={{ fontWeight: 500 }}>Status</InputLabel>
            <Select
              value={taskStatus}
              onChange={(e) => setTaskStatus(e.target.value)}
              label="Status"
              sx={{ 
                minHeight: 56,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.grey[300],
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
              }}
              MenuProps={{ 
                PaperProps: { 
                  sx: { 
                    mt: 1,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    borderRadius: 2
                  } 
                } 
              }}
            >
              {Object.values(TASK_STATUS).map(status => (
                <MenuItem key={status} value={status} sx={{ py: 1.5 }}>
                  <TaskStatusChip status={status} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel sx={{ fontWeight: 500 }}>Priority</InputLabel>
            <Select
              value={taskPriority}
              onChange={(e) => setTaskPriority(e.target.value)}
              label="Priority"
              sx={{ 
                minHeight: 56,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.grey[300],
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
              }}
              MenuProps={{ 
                PaperProps: { 
                  sx: { 
                    mt: 1,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    borderRadius: 2
                  } 
                } 
              }}
            >
              {Object.values(TASK_PRIORITY).map(priority => (
                <MenuItem key={priority} value={priority} sx={{ py: 1.5 }}>
                  <PriorityChip priority={priority} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Agenda Assignment */}
        <Box sx={{ mb: 4 }}>
          <FormControl fullWidth>
            <InputLabel sx={{ fontWeight: 500 }}>Assign to Agenda</InputLabel>
            <Select
              label="Assign to Agenda"
              value={typeof taskAgendaId === 'string' ? taskAgendaId : ''}
              onChange={e => setTaskAgendaId(e.target.value)}
              sx={{
                minHeight: 56,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.grey[300],
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    borderRadius: 2
                  }
                }
              }}
              renderValue={selected => {
                if (!selected) return 'Unassigned';
                const agenda = agendas.find(a => a.agendaId === selected);
                return agenda ? agenda.name : '';
              }}
            >
              <MenuItem value="">Unassigned</MenuItem>
              {agendas.map(agenda => (
                <MenuItem key={agenda.agendaId} value={agenda.agendaId}>
                  {agenda.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Team Assignment */}
        <Box sx={{ mb: 4 }}>
          <Autocomplete
            multiple
            options={eventParticipants}
            getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
            value={selectedAssignees}
            onChange={(event, newValue) => setSelectedAssignees(newValue)}
            renderOption={(props, option) => (
              <Box 
                component="li" 
                sx={{ 
                  '& > img': { mr: 2, flexShrink: 0 },
                  py: 1.5,
                  px: 2
                }} 
                {...props}
              >
                <Avatar 
                  src={option.avatar} 
                  sx={{ width: 32, height: 32, mr: 2, fontSize: 14 }}
                >
                  {option.firstName[0]}
                </Avatar>
                <Box>
                  <div style={{ fontWeight: 500 }}>{`${option.firstName} ${option.lastName}`}</div>
                  <div style={{ 
                    fontSize: '0.875rem', 
                    color: theme.palette.text.secondary,
                    marginTop: 2
                  }}>
                    {option.email}
                  </div>
                </Box>
              </Box>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  key={option.id || index}
                  avatar={
                    <Avatar 
                      src={option.avatar} 
                      sx={{ width: 24, height: 24, fontSize: 12 }}
                    >
                      {option.firstName[0]}
                    </Avatar>
                  }
                  label={`${option.firstName} ${option.lastName}`}
                  {...getTagProps({ index })}
                  sx={{ 
                    backgroundColor: theme.palette.primary.light,
                    color: theme.palette.primary.contrastText,
                    fontWeight: 500,
                    height: 32,
                    borderRadius: 2,
                    margin: '2px',
                    '& .MuiChip-deleteIcon': {
                      color: theme.palette.primary.contrastText,
                      '&:hover': {
                        color: theme.palette.primary.dark
                      }
                    }
                  }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Assign to Team Members"
                placeholder={eventParticipants.length === 0 ? "No users available" : "Select team members"}
                variant="outlined"
                InputLabelProps={{ sx: { fontWeight: 500 } }}
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    minHeight: 56,
                    alignItems: 'flex-start',
                    paddingTop: '8px',
                    paddingBottom: '8px',
                    paddingLeft: '14px',
                    paddingRight: '14px',
                    '& fieldset': {
                      borderColor: theme.palette.grey[300],
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main,
                    },
                    '& .MuiAutocomplete-input': {
                      minWidth: '30px !important',
                      padding: '4px 0px !important'
                    }
                  }
                }}
              />
            )}
            noOptionsText={eventParticipants.length === 0 ? "No users available" : "Type to search"}
            ListboxProps={{
              sx: {
                maxHeight: 200,
                '& .MuiAutocomplete-option': {
                  minHeight: 48
                }
              }
            }}
            sx={{ 
              '& .MuiInputBase-root': {
                minHeight: '56px !important',
                height: 'auto !important',
                flexWrap: 'wrap',
                alignContent: 'flex-start',
                gap: '2px',
                padding: '8px 14px !important',
                paddingRight: '39px !important',
                '&.Mui-focused': {
                  paddingRight: '39px !important'
                }
              },
              '& .MuiAutocomplete-tag': { 
                margin: '2px',
                maxWidth: 'calc(100% - 60px)'
              },
              '& .MuiAutocomplete-endAdornment': {
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)'
              },
              '& .MuiAutocomplete-clearIndicator': {
                marginRight: '2px'
              }
            }}
          />
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Time Selectors */}
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 3,
          mb: 2
        }}>
          <TextField
            label="Start Time (optional)"
            type="time"
            value={taskStartTime}
            onChange={(e) => setTaskStartTime(e.target.value)}
            fullWidth
            variant="outlined"
            InputLabelProps={{ shrink: true, sx: { fontWeight: 500 } }}
            inputProps={{ step: 1800 }}
            placeholder="--:--"
            sx={{ 
              '& .MuiOutlinedInput-root': {
                minHeight: 56,
                '& fieldset': {
                  borderColor: theme.palette.grey[300],
                },
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              }
            }}
          />
          <TextField
            label="End Time (optional)"
            type="time"
            value={taskEndTime}
            onChange={(e) => setTaskEndTime(e.target.value)}
            fullWidth
            variant="outlined"
            InputLabelProps={{ shrink: true, sx: { fontWeight: 500 } }}
            inputProps={{ step: 1800 }}
            placeholder="--:--"
            sx={{ 
              '& .MuiOutlinedInput-root': {
                minHeight: 56,
                '& fieldset': {
                  borderColor: theme.palette.grey[300],
                },
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              }
            }}
          />
        </Box>
      </DialogContent>

      <Divider sx={{ m: 0 }} />

      <DialogActions sx={{ 
        p: { xs: 3, sm: 4 }, 
        pt: 3,
        gap: 2,
        justifyContent: 'flex-end',
        backgroundColor: theme.palette.grey[50]
      }}>
        <Button 
          onClick={onClose} 
          sx={{ 
            fontWeight: 500,
            px: 3,
            py: 1,
            borderRadius: 2,
            textTransform: 'none',
            color: theme.palette.text.secondary,
            '&:hover': {
              backgroundColor: theme.palette.grey[100]
            }
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={onSave}
          variant="contained"
          disabled={!taskTitle}
          sx={{ 
            fontWeight: 600, 
            borderRadius: 2, 
            minWidth: 100,
            px: 3,
            py: 1,
            textTransform: 'none',
            boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
            '&:hover': {
              boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
            },
            '&:disabled': {
              backgroundColor: theme.palette.grey[300],
              boxShadow: 'none'
            }
          }}
        >
          {selectedTask ? 'Save Changes' : 'Add Task'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEditTaskDialog;